import { atom } from 'jotai';
import { CollectForm_Base } from '~/common/typescript/models/CollectForm';
import { parseFormContent, ParsedFormField } from '../utils/CollectFormParser';

export const selectedFormUUIDAtom = atom<string | null>(null);

export const availableFormsAtom = atom<CollectForm_Base[]>([]);

// Cache for individually loaded forms
export const singleFormsCacheAtom = atom<Record<string, CollectForm_Base>>({});

// Combined dictionary of all collect forms indexed by UUID
export const allCollectFormsMapAtom = atom<Record<string, CollectForm_Base>>((get) => {
  const forms = get(availableFormsAtom);
  const singleCache = get(singleFormsCacheAtom);
  const map: Record<string, CollectForm_Base> = { ...singleCache };
  forms.forEach((form) => {
    if (form.uuid) {
      map[form.uuid] = form;
    }
  });
  return map;
});

// Map of parsed fields indexed by form UUID and field key
export const collectFormFieldsMapAtom = atom<Record<string, Record<string, ParsedFormField>>>((get) => {
  const formsMap = get(allCollectFormsMapAtom);
  const result: Record<string, Record<string, ParsedFormField>> = {};

  Object.keys(formsMap).forEach((uuid) => {
    const form = formsMap[uuid];
    if (form) {
      const parsed = parseFormContent(form);
      if (parsed && parsed.fields) {
        const fieldsByKey: Record<string, ParsedFormField> = {};
        parsed.fields.forEach((field) => {
          if (field.key) {
            fieldsByKey[field.key] = field;
          }
        });
        result[uuid] = fieldsByKey;
      }
    }
  });

  return result;
});

// Pagination for collect forms
export interface PaginationInfo {
  page: number;
  pageSize: number;
  pageCount: number;
  total: number;
}

export const collectFormsPaginationAtom = atom<PaginationInfo>({
  page: 1,
  pageSize: 10,
  pageCount: 0,
  total: 0,
});

// Derived atom: get selected form data from availableForms based on UUID
export const selectedFormDataAtom = atom((get) => {
  const uuid = get(selectedFormUUIDAtom);
  const formsMap = get(allCollectFormsMapAtom);
  if (!uuid) return null;
  return formsMap[uuid] || null;
});

export const isMaximizedAtom = atom<boolean>(false);
